import React, { FC, ReactElement } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { observer } from "mobx-react";
import CircleIcon from "common/CircleIcon/CircleIcon";
import ZCBonuses from "assets/icons/zc-bonuses.svg";
import Account from "assets/icons/account.svg";
import { useStore } from "store";
import { useUrl } from "helpers/hooks";
import { cn } from "helpers/style";
import styles from "./Profile.module.scss";

const Auth = dynamic(() => import("common/Auth/Auth"), { ssr: false });
const Dropdown = dynamic(() => import("common/Dropdown/Dropdown"));

type Props = {
  sticky?: boolean;
};

const Profile: FC<Props> = observer(({ sticky }: Props): ReactElement => {
  const { user, notifications } = useStore();
  const { getLinkProps } = useUrl();
  const userId = user.profile?.id;
  const isOperator = user.profile?.isOperator;

  const logout = async e => {
    e && e.preventDefault();
    e && e.stopPropagation();

    await user.logout();
  };

  // todo: profile link
  if (user.isAuth && sticky) {
    return (
      <CircleIcon
        icon={<Account />}
        customCounter={
          user.isEmployeeMode || isOperator ? (
            <div
              className={cn([
                styles.profileBonuses,
                styles.profileBonusesEmployeeMode,
              ])}
            >
              {user.isEmployeeMode ? "Сотрудник" : "Оператор"}
            </div>
          ) : (
            user.profile.points !== null &&
            user.profile.points !== undefined && (
              <Link
                {...getLinkProps(`/personal/${userId}/mybonus/`, undefined, {
                  withRegion: false,
                  withOrigin: true,
                })}
                className={styles.profileBonuses}
                passHref
              >
                <>
                  <ZCBonuses />
                  {user.profile.points}
                </>
              </Link>
            )
          )
        }
        position="bottom"
      >
        <Link
          {...getLinkProps(`/personal/${userId}/`, undefined, {
            withRegion: false,
            withOrigin: true,
          })}
          className={styles.link}
          aria-label="Профиль"
          passHref
        />
      </CircleIcon>
    );
  }

  if (!user.isAuth)
    return (
      <CircleIcon
        aria-label="Профиль"
        as="button"
        onClick={() => {
          notifications.show({
            type: "modal",
            component: Auth,
          });
        }}
        icon={<Account />}
      />
    );

  return (
    <div className={styles.profile}>
      <Dropdown
        heading={
          <CircleIcon
            aria-label="Профиль"
            as="button"
            icon={<Account />}
            customCounter={
              user.isEmployeeMode || isOperator ? (
                <div
                  className={cn([
                    styles.profileBonuses,
                    user.isEmployeeMode
                      ? styles.profileBonusesEmployeeMode
                      : styles.profileBonusesIsOperator,
                  ])}
                >
                  {user.isEmployeeMode ? "Сотрудник" : "Оператор"}
                </div>
              ) : (
                <Link
                  {...getLinkProps(`/personal/${userId}/mybonus/`, undefined, {
                    withRegion: false,
                  })}
                  className={styles.profileBonuses}
                  passHref
                >
                  <>
                    <ZCBonuses />
                    {user.profile.points}
                  </>
                </Link>
              )
            }
            position="bottom"
          />
        }
        width="220px"
      >
        <ul className={styles.profileActionsList}>
          <li>
            <Link
              {...getLinkProps(`/personal/${userId}/`, undefined, {
                withRegion: false,
                withOrigin: true,
              })}
              className={styles.profileActionsLink}
              passHref
            >
              Профиль
            </Link>
          </li>

          <li>
            <Link
              {...getLinkProps(`/personal/${userId}/history/`, undefined, {
                withRegion: false,
                withOrigin: true,
              })}
              className={styles.profileActionsLink}
              passHref
            >
              Мои заказы
            </Link>
          </li>

          <li>
            <Link
              {...getLinkProps("/favorite/", "/favorite/", {
                withOrigin: true,
              })}
              className={styles.profileActionsLink}
              passHref
            >
              Мои подписки
            </Link>
          </li>

          <li>
            <a
              className={styles.profileActionsLink}
              href={getLinkProps("/").as as string}
              onClick={logout}
            >
              Выйти
            </a>
          </li>
        </ul>
      </Dropdown>
    </div>
  );
});

export default Profile;
